import styled, { keyframes, css } from "styled-components";
import { motion } from "framer-motion";
import { colors2, UI_BODY_SEMIBOLD_SM_DF } from "oolib";
import { globalScrollbarStyling } from "oolib/dist/v2/themes/globalStyles";

/** Panel width in rem */
export const LIGHTBOX_PANEL_WIDTH = 32;
/** Fraction of panel width visible behind the overlapping panel (0–1) */
export const LIGHTBOX_PANEL_MAX_OFFSET = 0.5;
/** Minimum offset fraction when panels need to compress to fit viewport */
export const LIGHTBOX_PANEL_MIN_OFFSET = 0.2;
/** Pixel padding from the right edge of the viewport */
export const LIGHTBOX_VIEWPORT_PADDING = 16;

/** Shared spring config so outer container and inner panel animate in lockstep */
export const INLINE_PANEL_TRANSITION = { type: "spring" as const, stiffness: 400, damping: 40, mass: 1 };

/** Outer shell: shadow, sizing, and border-radius */
export const StyledLightboxPanel = styled.div<{ $cascade?: boolean }>`
    max-height: 80vh;
    display: flex;
    flex-direction: column;
    box-shadow: 0 0 20px hsla(0, 0%, 0%, 0.15);
    border-radius: 1.2rem;
    overflow: hidden;
    background: ${colors2.white};
    min-width: ${({ $cascade }) => ($cascade ? `${LIGHTBOX_PANEL_WIDTH}rem` : "auto")};
`;

/** Outer shell for inline (multilayer) mode — animates width via framer-motion */
export const StyledInlinePanel = styled(motion.div)`
    max-height: 80vh;
    display: flex;
    flex-direction: row;
    border-radius: 1.2rem;
    overflow: clip;
    box-shadow: 0 0 20px hsla(0, 0%, 0%, 0.15);
    background: ${colors2.white};
    will-change: width, height;
`;

/** Inner content: border, padding, and scrollbar styling */
export const StyledLightboxPanelContent = styled.div<{ $cascade?: boolean }>`
    position: relative;
    outline: none;
    border-radius: 1.2rem;
    background: ${colors2.white};
    width: ${({ $cascade }) => ($cascade ? `${LIGHTBOX_PANEL_WIDTH}rem` : "auto")};
    border: 1px solid ${colors2.grey10};
    padding: 0.2rem;
    display: flex;
    flex-direction: column;
    overflow-y: auto;

    ${globalScrollbarStyling}
`;

/**
 * Clickable hover overlay with backdrop blur for non-top inline panels.
 * Rendered as a sibling div inside the panel content. Hidden by default;
 * revealed when the parent panel is hovered via the CSS rule below.
 */
export const StyledPanelOverlay = styled.div`
    position: absolute;
    inset: 0;
    border-radius: inherit;
    backdrop-filter: blur(10.9px);
    background: rgba(255, 255, 255, 0.4);
    opacity: 0;
    transition: opacity 0.15s ease;
    pointer-events: none;
    cursor: pointer;
    z-index: 10;
`;

export const StyledBackButton = styled.button`
    position: absolute;
    left: calc(var(--panel-offset, ${LIGHTBOX_PANEL_WIDTH * LIGHTBOX_PANEL_MAX_OFFSET}rem) / 2 + 24px);
    top: 50%;
    transform: translate(-50%, -50%) scale(0.9);
    transform-origin: center center;
    z-index: 11;
    width: 53px;
    height: 53px;
    border-radius: 50%;
    border: none;
    background: #00000024;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.2s ease, transform 0.2s ease, left 0.2s ease;
`;

/**
 * Dims all items in an inline panel except the active one ([data-active]).
 * Topmost panel is naturally excluded — it never has a [data-active] child.
 */
const inlinePanelDimming = css`
    [data-lightbox-item],
    [data-lightbox-header],
    [data-lightbox-divider] {
        transition: opacity 0.15s ease;
    }

    &:has([data-active]):not([data-children-exiting]) [data-lightbox-item]:not([data-active]),
    &:has([data-active]):not([data-children-exiting]) [data-lightbox-header],
    &:has([data-active]):not([data-children-exiting]) [data-lightbox-divider] {
        opacity: 0.5;
    }
`;

/** Reveal the overlay and back button when the panel is hovered */
const inlinePanelOverlayHover = css`
    &:hover ${StyledPanelOverlay} {
        opacity: 1;
        pointer-events: auto;
    }

    &:hover ${StyledBackButton} {
        opacity: 1;
        left: calc(var(--panel-offset, ${LIGHTBOX_PANEL_WIDTH * LIGHTBOX_PANEL_MAX_OFFSET}rem) / 2);
        transform: translate(-50%, -50%) scale(1);
        pointer-events: auto;
    }
`;

/** Wrapper for trigger when asChild is false */
export const StyledTriggerWrapper = styled.span`
    display: inline-flex;
    // Allow the wrapper to shrink within its flex parent and cap at the parent's
    // width, so a constrained trigger (e.g. a truncating tag pill) can actually
    // shrink + ellipsize instead of overflowing the row.
    min-width: 0;
    max-width: 100%;
`;

const shadowIn = keyframes`
    from { opacity: 0; transform: translateX(0.5rem); }
    to   { opacity: 1; transform: translateX(0); }
`;

const shadowOut = keyframes`
    from { opacity: 1; transform: translateX(0); }
    to   { opacity: 0; transform: translateX(0.5rem); }
`;

/** Root panel content in inline mode */
export const StyledInlineRootPanelContent = styled(StyledLightboxPanelContent)`
    isolation: isolate;
    ${inlinePanelDimming}
    ${inlinePanelOverlayHover}
`;

/** Inner content panel for inline child panels */
export const StyledInlineChildPanelContent = styled(StyledLightboxPanelContent)`
    z-index: 0;
    ${inlinePanelDimming}
    ${inlinePanelOverlayHover}
`;

/** Left-edge gradient shadow for inline child panels */
export const StyledPanelShadow = styled.div`
    position: absolute;
    top: 0;
    right: calc(100% - 1.2rem);
    width: 4.2rem;
    height: 100%;
    background: linear-gradient(90deg, rgba(230, 230, 230, 0) 0%, rgba(230, 230, 230, 0.5) 100%);
    pointer-events: none;
    z-index: -1;
    animation: ${shadowIn} 0.25s ease-out forwards;

    &[data-exiting] {
        animation: ${shadowOut} 0.25s ease-in forwards;
    }
`;

/** Title displayed at the top of the lightbox content */
export const StyledLightboxTitle = styled(UI_BODY_SEMIBOLD_SM_DF)`
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    white-space: normal;
    line-height: 1.2;
    min-width: 0;
    flex: 1;
    padding: 0.5rem 0.6rem 0.9rem;
`;
